/*
 * Product page helpers (Rang Sitaray):
 *  <rs-countdown>  ticks the sale timer and copies the discount code (block "RS Sale countdown").
 * Safe in the theme editor: re-initialises on section load and cleans up on unload.
 */
(() => {
  if (customElements.get('rs-countdown')) return;

  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  class RsCountdown extends HTMLElement {
    connectedCallback() {
      this.destroy();

      this.mode = this.dataset.mode === 'fixed' ? 'fixed' : 'daily';
      this.hideEnded = this.dataset.hideEnded === 'true';
      this.digits = {
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };
      this.copyButton = this.querySelector('.rs-countdown__copy');
      this.status = this.querySelector('.rs-countdown__status');

      this.target = this.mode === 'fixed' ? this.parseFixed(this.dataset.end) : null;
      this.dailyTime = this.mode === 'daily' ? this.parseDaily(this.dataset.end) : null;

      if (this.mode === 'fixed' && !this.target) this.hidden = true;
      if (this.mode === 'daily' && !this.dailyTime) this.dailyTime = { hours: 23, minutes: 59 };

      this.onCopy = () => this.copyCode();
      if (this.copyButton) this.copyButton.addEventListener('click', this.onCopy);

      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      clearInterval(this.timer);
      clearTimeout(this.copyTimer);
      if (this.copyButton && this.onCopy) this.copyButton.removeEventListener('click', this.onCopy);
      this.timer = null;
    }

    parseDaily(value) {
      const match = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
      if (!match) return null;
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      return hours < 24 && minutes < 60 ? { hours, minutes } : null;
    }

    parseFixed(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})$/.exec((value || '').trim());
      if (!match) return null;
      const [, year, month, day, hours, minutes] = match.map(Number);
      return new Date(year, month - 1, day, hours, minutes, 0, 0);
    }

    remaining() {
      const now = new Date();
      if (this.mode === 'fixed') return this.target - now;
      const end = new Date(now);
      end.setHours(this.dailyTime.hours, this.dailyTime.minutes, 0, 0);
      if (end <= now) end.setDate(end.getDate() + 1);
      return end - now;
    }

    tick() {
      if (this.mode === 'fixed' && !this.target) return;
      const ms = this.remaining();
      if (ms <= 0) {
        if (this.hideEnded) this.hidden = true;
        this.render(0);
        return;
      }
      this.hidden = false;
      this.render(ms);
    }

    render(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      this.digits.hours.textContent = pad(Math.floor(totalSeconds / 3600));
      this.digits.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
      this.digits.seconds.textContent = pad(totalSeconds % 60);
    }

    async copyCode() {
      const code = this.dataset.code;
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        const field = document.createElement('textarea');
        field.value = code;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        try {
          document.execCommand('copy');
        } catch (copyError) {
          // Copying is not available here; the confirmation text still shows the code.
        }
        field.remove();
      }

      const text = this.copyButton.querySelector('.rs-countdown__copy-text');
      const original = this.copyButton.dataset.label;
      text.textContent = this.dataset.copiedLabel || 'CODE COPIED!';
      if (this.status) this.status.textContent = `${this.dataset.copiedLabel} ${code}`;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        text.textContent = original;
        if (this.status) this.status.textContent = '';
      }, 2500);
    }
  }

  customElements.define('rs-countdown', RsCountdown);
})();
